import { useRef, useState } from 'react';
import SafeImage from '@ui/SafeImage';
import Breadcrumb, { BreadcrumbItem } from '@c/ui/Breadcrumb';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '@util/firebase';
import { logError } from '@util/logError';
import { MasterDocument } from '@util/firestore/master';
import NotifyMeModal from './NotifyMeModal';

interface HeroSectionProps {
  master: MasterDocument;
  sellUrl: string;
  hasListings?: boolean;
}

/**
 * Slugifies a category string for use in URLs.
 * e.g. "Photo & Video Lenses" → "photo-video-lenses"
 */
function slugifyCategory(cat: string): string {
  return cat
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * HeroSection — Top-of-page hero with product image, title, breadcrumbs, and CTA card.
 *
 * Renders a three-column grid on desktop (image | title+breadcrumbs | CTA).
 * Stacks vertically on mobile (below md breakpoint).
 */
export default function HeroSection({ master, sellUrl, hasListings = true }: HeroSectionProps) {
  const { user, userDoc } = useAuth();
  const { showErrorToast } = useToastContext();
  // null = no local override, use userDoc. true/false = optimistic toggle.
  const [watchOverride, setWatchOverride] = useState<boolean | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [guestSubmitted, setGuestSubmitted] = useState(false);
  // #1617 MEDIUM-2: a fast double-click otherwise races a watch dedup read
  // against an unwatch delete (heart ends up ON with no modelWatchers doc
  // behind it). Ref is the load-bearing guard (checked synchronously before
  // any state update commits); the mirrored state drives the disabled prop.
  const watchRequestPendingRef = useRef(false);
  const [watchRequestPending, setWatchRequestPending] = useState(false);

  const alreadyWatching = watchOverride !== null
    ? watchOverride
    : (guestSubmitted || (userDoc?.watchedModels?.includes(master.id) ?? false));

  // Resolve image source: prefer thumbnail, fall back to first image in array
  const imageSrc =
    master.thumbnail ||
    (master.images && master.images.length > 0 ? master.images[0] : '');

  // Build breadcrumb items from categories
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' },
  ];

  if (master.categories && master.categories.length > 0) {
    const cat1 = master.categories[0];
    breadcrumbItems.push({
      label: cat1,
      href: `/c/${slugifyCategory(cat1)}`,
    });

    if (master.categories.length > 1) {
      const cat2 = master.categories[1];
      breadcrumbItems.push({
        label: cat2,
        href: `/c/${slugifyCategory(cat2)}`,
        current: true,
        currentCss: 'text-brand-dark-gray text-[1.4rem]',
      });
    }
  }

  const handleHeartClick = async () => {
    if (!user || !userDoc) {
      setShowModal(true);
      return;
    }

    // #1617 MEDIUM-2: ignore a click while the previous one is still in
    // flight — see the ref comment above.
    if (watchRequestPendingRef.current) return;
    watchRequestPendingRef.current = true;
    setWatchRequestPending(true);

    try {
      if (alreadyWatching) {
        // #1617 HIGH-1: the API call now requires a verified token and can
        // genuinely fail (401/403/500), so it must run FIRST and its result
        // must be checked. Only once the server confirms the modelWatchers
        // doc is gone do we update the cached array and flip the heart —
        // that way there's never a window where the two disagree, and a
        // failure never reads as success.
        try {
          const res = await fetch('/api/master/unwatch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              masterId: master.id,
              uid: userDoc.uid,
            }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to unwatch this model');
          }
          await updateDoc(doc(db, 'users', userDoc.uid), {
            watchedModels: arrayRemove(master.id),
          });
          setWatchOverride(false);
        } catch (err) {
          logError(err);
          showErrorToast("Couldn't stop watching this item. Please try again.");
        }
      } else {
        try {
          const res = await fetch('/api/master/watch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              masterId: master.id,
              masterTitle: master.title,
              email: userDoc.email,
              uid: userDoc.uid,
            }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to watch this model');
          }
          await updateDoc(doc(db, 'users', userDoc.uid), {
            watchedModels: arrayUnion(master.id),
          });
          setWatchOverride(true);
        } catch (err) {
          logError(err);
          showErrorToast("Couldn't set up notifications for this item. Please try again.");
        }
      }
    } finally {
      watchRequestPendingRef.current = false;
      setWatchRequestPending(false);
    }
  };

  return (
    <section
      aria-label={`${master.title} - Hero`}
      className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
    >
      <div className={`grid grid-cols-1 ${hasListings ? 'md:grid-cols-[calc(30%-2rem)_calc(45%-2rem)_calc(25%-2rem)]' : 'md:grid-cols-[calc(35%-1.5rem)_calc(65%-1.5rem)]'} gap-[3rem] items-center`}>

        {/* Left column: Product image.
            Mobile (#1453 §3.2): shrink to a compact thumbnail so the buy box
            rises toward the first phone screen. md+ (desktop/tablet) restores
            the original 24rem/30rem sizing — pixel-unchanged. */}
        <div className="flex items-center justify-center bg-[#f8f8f8] rounded-[1.2rem] p-[1.6rem] min-h-[12rem] md:min-h-[24rem]">
          {imageSrc ? (
            <SafeImage
              src={imageSrc}
              alt={master.title}
              width={300}
              height={300}
              classes="object-contain w-full h-auto max-h-[16rem] md:max-h-[30rem]"
              priority
              sizes="(max-width: 768px) 80vw, 300px"
            />
          ) : (
            <SafeImage
              src="/not_found.svg"
              alt={master.title}
              width={300}
              height={300}
              classes="object-contain w-full h-auto max-h-[16rem] md:max-h-[30rem] opacity-40"
              priority
              sizes="(max-width: 768px) 80vw, 300px"
            />
          )}
        </div>

        {/* Center column: Title + Breadcrumbs + Watch heart */}
        <div className="flex flex-col gap-[1.2rem]">
          <p className="text-[2.8rem] sm:text-[3.2rem] font-extrabold text-black leading-[1.2]">
            {(() => {
              const words = master.title.split(' ');
              const leading = words.slice(0, -1).join(' ');
              const lastWord = words[words.length - 1];
              return (
                <>
                  {leading && <>{leading} </>}
                  <span className="inline-flex items-center gap-[0.4rem] whitespace-nowrap">
                    {lastWord}
                    <button
                      onClick={handleHeartClick}
                      disabled={watchRequestPending}
                      className={`inline-flex align-middle p-[0.2rem] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
                        alreadyWatching ? 'text-red-500' : 'text-gray-400'
                      }`}
                      aria-label={alreadyWatching ? 'Stop watching this product' : 'Watch this product'}
                      title={alreadyWatching ? 'Stop watching' : 'Get notified'}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill={alreadyWatching ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </button>
                  </span>
                </>
              );
            })()}
          </p>
          <div className="text-[1.4rem]">
            <Breadcrumb
              items={breadcrumbItems}
              color="text-brand-secondary"
            />
          </div>
        </div>

        {/* Right column: "Own One Like This?" CTA card — hidden when no listings (moved to SellYoursModule).
            Mobile (#1453 §3.2, Bill's OPTION 1): this in-hero sell CTA is
            intentionally dropped below md so price + Add to Cart reach the first
            phone screen. On mobile the buy card still shows the informational
            "Sell your … on GearFocus" line (plain text, not a link), and the
            global sell entry point stays in the site header — so the sell path
            is not lost, only this in-context card. md+ shows this card as
            before — desktop pixel-unchanged. (When there are no listings this
            card isn't rendered at all; SellYoursModule covers that path via
            EmptyStateContainer.) */}
        {hasListings && (
          <div
            className="bg-[#fbfbfb] rounded-[2rem] shadow-[2px_2px_15px_0px_rgba(0,0,0,0.25)] p-[2.4rem] hidden md:flex flex-col gap-[1.2rem]"
          >
            <p className="text-[1.8rem] font-extrabold text-black leading-[1.3]">
              Own One Like This?
            </p>
            <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5]">
              Make room for more gear in minutes!
            </p>
            <a
              href={sellUrl}
              className="inline-block bg-black text-white font-bold rounded-[1rem] px-[2.4rem] py-[0.8rem] text-[1.4rem] text-center hover:bg-gray-800 transition-colors duration-150"
            >
              Sell your {master.brand} {master.model} on GearFocus
            </a>
          </div>
        )}

      </div>

      {/* Guest notify modal — reuses shared NotifyMeModal */}
      <NotifyMeModal
        master={master}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => setGuestSubmitted(true)}
      />
    </section>
  );
}
